import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import useLocalization from "#src/hooks/useLocalization";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  FilterBuilderFieldsType,
  FilterDisplay,
  Skeleton,
} from "@validereinc/common-components";
import {
  LockRecordActionType,
  ReportingGroupAdapter,
  Resources,
} from "@validereinc/domain";
import {
  areAllObjectValuesEmpty,
  toTitleCaseString,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import { isEmpty, omit, pick } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./WorkflowActionUpdateRecordStatus.module.scss";

const cx = classNames.bind(styles);

const { AccordionPanel } = Accordion;

type PeriodFields = {
  period_start?: string;
  period_end?: string;
  year_month?: string;
};

export const WorkflowActionUpdateRecordStatus = ({
  recordFilter,
}: {
  recordFilter: LockRecordActionType["record_filter"];
}) => {
  const { localize } = useLocalization();

  const { data: reportingGroupsData, isLoading: isReportingGroupsLoading } =
    useQuery({
      queryKey: [Resources.REPORTING_GROUP],
      queryFn: () => ReportingGroupAdapter.getList({}),
    });
  const reportingGroups = reportingGroupsData?.data ?? [];
  const reportingGroupsMap = useMemo(() => {
    return Object.fromEntries(
      reportingGroups.map((category) => [category.id, category.name])
    );
  }, [reportingGroups]);

  const shapeFilterBuilderFilters = (filter: Record<string, any>) => {
    const periodFields: PeriodFields = {
      ...(filter.year_month?.$gte && {
        period_start: filter.year_month.$gte,
      }),
      ...(filter.year_month?.$lte && {
        period_end: filter.year_month.$lte,
      }),
      ...(!filter.year_month?.$gte &&
        !filter.year_month?.$lte &&
        filter.year_month &&
        !areAllObjectValuesEmpty(filter.year_month) && {
          year_month: filter.year_month,
        }),
    };

    const basicFields = Object.fromEntries(
      Object.entries(pick(filter, ["asset_type", "reporting_group_id"]))
        .filter(([_, value]) => value !== "" && value !== undefined)
        .map(([key, value]) => {
          if (key === "reporting_group_id") {
            return [key, reportingGroupsMap[value] ?? value];
          }
          if (key === "asset_type") {
            if (Array.isArray(value?.$in)) {
              return [
                key,
                value.$in.map((v) => toTitleCaseString(localize(v))),
              ];
            }
            return [key, toTitleCaseString(localize(value))];
          }
          return [key, toTitleCaseString(localize(value))];
        })
    );

    const filterBuilderFilters = omit(filter, [
      "year_month",
      "asset_type",
      "reporting_group_id",
    ]);

    const shapedFilterBuilderFilters = Object.fromEntries(
      Object.entries(filterBuilderFilters)
        .filter(([_, value]) => value !== "" && value !== undefined)
        .map(([key, value]) => {
          // Filter builder filters are in the format of <asset_type>.custom_attribute.value
          const newKey = key.split(".").slice(1).join(".");
          return [newKey, value];
        })
    );

    return {
      ...periodFields,
      ...basicFields,
      ...shapedFilterBuilderFilters,
    };
  };

  const [filterBuilderFilters, setFilterBuilderFilters] = useState(
    shapeFilterBuilderFilters(recordFilter)
  );

  const { customAttributeFilterBuilderFieldDefinitions } =
    useCustomAttributeFiltersV2({
      assetType: recordFilter.asset_type,
    });

  useEffect(() => {
    if (isEmpty(reportingGroupsMap)) {
      return;
    }
    setFilterBuilderFilters(shapeFilterBuilderFilters(recordFilter));
  }, [reportingGroupsMap]);

  const fields: FilterBuilderFieldsType = {
    ...(recordFilter.year_month?.$gte
      ? {
          period_start: {
            data_type: "string",
            display_name: "Period Start",
          },
        }
      : {}),
    ...(recordFilter.year_month?.$lte
      ? {
          period_end: {
            data_type: "string",
            display_name: "Period End",
          },
        }
      : {}),
    ...(!recordFilter.year_month?.$gte &&
    !recordFilter.year_month?.$lte &&
    !areAllObjectValuesEmpty(recordFilter.year_month)
      ? {
          year_month: {
            data_type: "string",
            display_name: "Year Month",
          },
        }
      : {}),
    ...(recordFilter.asset_type
      ? {
          asset_type: {
            data_type: "string",
            display_name: "Asset Type",
          },
        }
      : {}),
    ...(recordFilter.reporting_group_id
      ? {
          reporting_group_id: {
            data_type: "string",
            display_name: "Reporting Group",
          },
        }
      : {}),
    ...customAttributeFilterBuilderFieldDefinitions,
  };

  return (
    <Accordion defaultActiveKeys={["locked-record-filters"]}>
      <AccordionPanel
        dataKey="locked-record-filters"
        title="Locked Record Filters"
      >
        <Skeleton isLoading={isReportingGroupsLoading}>
          {!isEmpty(filterBuilderFilters) && (
            <FilterDisplay
              className={cx("filter-display")}
              variant="vertical"
              fields={fields}
              filter={filterBuilderFilters}
            />
          )}
        </Skeleton>
      </AccordionPanel>
    </Accordion>
  );
};
